import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * `/trainer-pay/:token` — a trainer's own month-to-date pay, and the only page
 * in the product whose viewer is a TRAINER rather than staff.
 *
 * UNAUTHENTICATED by design and deliberately outside `<RequireAuth>` in
 * `App.tsx`, like `/login`. There is no trainer login and no trainer role;
 * the signed token in the path is the whole capability, and it is scoped to
 * exactly one trainer for 30 days
 * (`@layer/canonical-data/trainer-pay-tokens`). Giving a trainer a staff
 * session instead would have handed them `/api/dispatch/*` too, which the
 * page gate deliberately does not cover.
 *
 * Uses raw `fetch` rather than `../lib/api`: that client attaches a Supabase
 * bearer and bounces a 401 to `/login`, neither of which is right here.
 *
 * ## Why the number is labelled PROVISIONAL, in words
 *
 * Guard T6 (`skills/cron-driven/trainerops/GUARDS.md`) says the first pay
 * figure a trainer sees is the APPROVED statement. Mario amended it on
 * 2026-08-20 to admit exactly this page — a clearly-labelled self-serve
 * figure — while leaving the agent still barred from quoting one. The label
 * is the whole basis of that carve-out, so it is prose at the top of the
 * page, not a subtle chip.
 *
 * And the figure genuinely moves BOTH WAYS. Four of the five components the
 * engine emits accrue strictly per completed session and only climb. The
 * MEMBERSHIP bonus does not: it is a pool shared across whoever trains that
 * member, capped at the cycle allowance, so a second trainer delivering later
 * in the month reduces this trainer's already-earned share (pinned in
 * `packages/canonical-actions/tests/payroll/payroll-month-to-date.test.ts`).
 * A trainer who watches a number fall with no explanation files a dispute; a
 * trainer who was told it can happen does not.
 */

interface PayLine {
  readonly component: string;
  readonly quantity: number;
  readonly amount_minor: number;
  readonly currency: string;
}

interface PayGap {
  readonly kind: string;
  readonly detail: string;
}

/**
 * One delivered session and the arithmetic behind its share. `value_minor` is
 * the session's own revenue basis as the ENGINE computed it (pack price ÷
 * sessions in the pack, membership price ÷ cycle) — not a number this page
 * derives, so the row and the component line above it cannot disagree.
 */
interface PaySession {
  readonly booking_id: string;
  readonly component: string;
  readonly value_minor: number;
  readonly share_minor: number;
  readonly currency: string;
  readonly rate_pct: number | null;
  /** The ladder could not price this session, so it drew the flat rate. */
  readonly unpriced: boolean;
  /** A late cancellation whose credit the studio kept — paid, not delivered. */
  readonly late_cancelled: boolean;
  readonly starts_at: string | null;
  readonly member_name: string | null;
  readonly session_type: string | null;
}

interface PayStatement {
  readonly ok: true;
  readonly trainer_display_name: string;
  readonly month: string;
  readonly month_start: string | null;
  readonly as_of: string;
  readonly provisional: boolean;
  readonly total_minor: number;
  readonly payable_sessions: number;
  readonly no_show_sessions: number;
  readonly lines: ReadonlyArray<PayLine>;
  readonly data_gaps: ReadonlyArray<PayGap>;
  readonly unattributed_gaps: number;
  readonly sessions: ReadonlyArray<PaySession>;
  readonly sessions_subtotal_minor: number;
}

const COMPONENT_LABELS: Readonly<Record<string, string>> = {
  session_revenue_share: "Sessions delivered",
  hourly: "Hours worked",
  pr_session_fixed: "PR sessions",
  unpriced_session_fixed: "Sessions at the flat rate",
  bonus_new_signup: "Bonus · new sign-up",
  bonus_renewal: "Bonus · renewal",
  adjustment: "Adjustment",
};

/** The components whose value can go DOWN as the rest of the month lands. */
const SHARED_POOL_COMPONENTS = new Set([
  "bonus_new_signup",
  "bonus_renewal",
]);

/** Sessions vs hours — the hourly model's quantity is not a session count. */
function quantityLabel(component: string, quantity: number): string {
  if (component === "hourly") {
    return `${quantity} ${quantity === 1 ? "hour" : "hours"}`;
  }
  if (component === "adjustment") return "";
  return `${quantity} ${quantity === 1 ? "session" : "sessions"}`;
}

function pounds(minor: number): string {
  const sign = minor < 0 ? "−" : "";
  return `${sign}£${(Math.abs(minor) / 100).toFixed(2)}`;
}

/** "1 August" — the period start, spelled out. */
function monthStartLabel(monthStart: string | null, month: string): string {
  const iso = monthStart ?? `${month}-01`;
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(d);
}

/** "Tue 26 Aug · 09:00" — a session's slot start, in studio time. */
function sessionWhen(iso: string | null): string {
  if (iso === null) return "Date unavailable";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date unavailable";
  const day = new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "Europe/London",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  }).format(d);
  return `${day} · ${time}`;
}

/** "studio" -> "Studio session". The catalog carries lowercase slugs. */
function sessionTypeLabel(raw: string | null): string | null {
  if (raw === null || raw.trim() === "") return null;
  const t = raw.trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function asOfLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/London",
  }).format(d);
}

export function TrainerPay(): JSX.Element {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<PayStatement | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "invalid" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/trainer-pay/${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        if (res.status === 403) {
          setState("invalid");
          return;
        }
        if (!res.ok) {
          setState("error");
          return;
        }
        setData((await res.json()) as PayStatement);
        setState("ok");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === "loading") {
    return (
      <div className="tpay-card">
        <p className="tpay-muted">Loading…</p>
      </div>
    );
  }

  if (state === "invalid") {
    // One message for expired, tampered, revoked and deleted-trainer alike —
    // the endpoint deliberately does not say which, and neither does this.
    return (
      <div className="tpay-card">
        <h1>This link isn&apos;t valid any more</h1>
        <p>
          Pay links stop working after 30 days. Ask the studio for a fresh one
          and it&apos;ll open straight away.
        </p>
      </div>
    );
  }

  if (state === "error" || data === null) {
    return (
      <div className="tpay-card">
        <h1>Something went wrong</h1>
        <p>Try again in a moment, or let the studio know if it keeps happening.</p>
      </div>
    );
  }

  const hasSharedPool = data.lines.some((l) =>
    SHARED_POOL_COMPONENTS.has(l.component),
  );

  return (
    <div className="tpay-card">
      <p className="tpay-eyebrow">{data.trainer_display_name}</p>
      <h1>Your pay so far this month</h1>
      <p className="tpay-period">
        {monthStartLabel(data.month_start, data.month)} to today · as at{" "}
        {asOfLabel(data.as_of)}
      </p>

      <div className="tpay-total">
        <div className="tpay-total-value">{pounds(data.total_minor)}</div>
        <div className="tpay-total-label">
          {data.payable_sessions}{" "}
          {data.payable_sessions === 1 ? "session" : "sessions"} counted
          {data.no_show_sessions > 0
            ? ` · ${data.no_show_sessions} no-show${data.no_show_sessions === 1 ? "" : "s"}`
            : ""}
        </div>
      </div>

      {/* The T6 carve-out lives or dies on this being unmissable and in
          plain words. Do not demote it to a chip or a tooltip. */}
      <div className="tpay-provisional">
        <strong>This is a provisional figure, not a payslip.</strong> It&apos;s
        worked out from the sessions completed so far this month and it can
        still change — including going <em>down</em>
        {hasSharedPool ? (
          <>
            , because bonus is shared out across whoever trains that member
          </>
        ) : null}
        . Nothing is final until the studio approves the month, and the
        approved statement is what gets paid.
      </div>

      {data.lines.length === 0 ? (
        <p className="tpay-muted">
          No sessions counted yet this month. Anything you deliver will appear
          here once it&apos;s marked complete.
        </p>
      ) : (
        <table className="tpay-lines">
          <tbody>
            {data.lines.map((l, i) => (
              <tr key={`${l.component}-${i}`}>
                <td>
                  {COMPONENT_LABELS[l.component] ?? l.component}
                  {SHARED_POOL_COMPONENTS.has(l.component) ? (
                    <span className="tpay-flag" title="Shared across trainers">
                      {" "}
                      · can still change
                    </span>
                  ) : null}
                  <span className="tpay-qty">
                    {quantityLabel(l.component, l.quantity)}
                  </span>
                </td>
                <td className="tpay-amount">{pounds(l.amount_minor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Per-session breakdown — "show me how the number is worked out, for
          each session". Only the per-session components appear here: `hourly`
          pays per SLOT (two members in one slot is one payable hour) and the
          bonuses are earned per pack and shared across trainers, so neither is
          a per-session quantity and forcing them into these rows would misstate
          them. Whatever they contribute is named in the remainder line below
          rather than left as an unexplained gap between this list and the
          total. */}
      {data.sessions.length > 0 ? (
        <div className="tpay-breakdown">
          <h2 className="tpay-breakdown-title">Every session, one by one</h2>
          <ol className="tpay-sessions">
            {data.sessions.map((sess) => {
              const type = sessionTypeLabel(sess.session_type);
              return (
                <li className="tpay-session" key={sess.booking_id}>
                  <div className="tpay-session-head">
                    <span className="tpay-session-when">
                      {sessionWhen(sess.starts_at)}
                    </span>
                    <span className="tpay-session-share">
                      {pounds(sess.share_minor)}
                    </span>
                  </div>
                  <div className="tpay-session-sub">
                    {sess.member_name ?? "Member"}
                    {type !== null ? ` · ${type}` : ""}
                    {/* Say it plainly. A trainer reading a name against a
                        session they know did not happen needs the line to
                        explain itself rather than look like a mistake. */}
                    {sess.late_cancelled ? (
                      <span className="tpay-latecancel">
                        {" "}
                        · late cancellation
                      </span>
                    ) : null}
                  </div>
                  <div className="tpay-session-maths">
                    {sess.component === "pr_session_fixed" ? (
                      <>PR session · fixed rate</>
                    ) : sess.unpriced ? (
                      /* Say WHY it is a flat rate. "£20.00" with no reason
                         reads as an arbitrary number on a pay statement. */
                      <>
                        Session value couldn&apos;t be worked out · flat rate ={" "}
                        {pounds(sess.share_minor)}
                      </>
                    ) : sess.value_minor > 0 && sess.rate_pct !== null ? (
                      <>
                        {pounds(sess.value_minor)} session value ·{" "}
                        {sess.rate_pct}% = {pounds(sess.share_minor)}
                      </>
                    ) : (
                      /* A zero basis is never silently rendered as "£0.00 ×
                         25%" — that reads like a session worth nothing rather
                         than one we could not price. The data-gaps block below
                         carries the reason. */
                      <>Session value not yet available — see the note below</>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="tpay-sessions-subtotal">
            <span>
              {data.sessions.length}{" "}
              {data.sessions.length === 1 ? "session" : "sessions"} above
            </span>
            <span className="tpay-amount">
              {pounds(data.sessions_subtotal_minor)}
            </span>
          </div>
          {data.total_minor !== data.sessions_subtotal_minor ? (
            <div className="tpay-sessions-remainder">
              <span>Bonuses and other components</span>
              <span className="tpay-amount">
                {pounds(data.total_minor - data.sessions_subtotal_minor)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Gaps are shown, never swallowed. A trainer who can see "3 sessions
          not yet attributed" raises it while it is still fixable; a trainer
          who cannot just sees a number that is quietly too low. */}
      {data.data_gaps.length > 0 || data.unattributed_gaps > 0 ? (
        <div className="tpay-gaps">
          <h2>Not counted yet</h2>
          <p className="tpay-muted">
            These need the studio to sort out before the month is approved.
            Mention them if they look wrong.
          </p>
          {data.data_gaps.length > 0 ? (
            <ul>
              {data.data_gaps.map((g, i) => (
                <li key={`${g.kind}-${i}`}>{g.detail}</li>
              ))}
            </ul>
          ) : null}
          {/* Anything the server could not tie to this trainer's own rows is
              counted rather than dropped — a low figure with no explanation is
              worse than a vague one. */}
          {data.unattributed_gaps > 0 ? (
            <p className="tpay-muted">
              {data.unattributed_gaps} further{" "}
              {data.unattributed_gaps === 1 ? "item is" : "items are"} still
              being sorted out for the studio this month. Some of{" "}
              {data.unattributed_gaps === 1 ? "it" : "them"} may affect your
              figure.
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="tpay-foot">
        Questions about any of this go to the studio — this page is just a
        read-out.
      </p>
    </div>
  );
}
